import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  NotificationDTO,
  NotificationListResponse,
} from "@shared/types/notifications";
import { apiClient, ApiError } from "./client";

// One query key shared by the center page AND the header bell, so a mark-read
// anywhere updates the badge everywhere.
const notificationsKey = ["notifications"] as const;

const POLL_MS = 30_000;

export function useNotifications() {
  return useQuery<NotificationListResponse, ApiError>({
    queryKey: notificationsKey,
    queryFn: () => apiClient<NotificationListResponse>("/api/notifications"),
    refetchInterval: POLL_MS,
  });
}

/** Count unread items in a cached response (used for optimistic badge math). */
function unreadIn(items: NotificationDTO[]): number {
  return items.reduce((acc, n) => (n.readAt ? acc : acc + 1), 0);
}

export function useMarkRead() {
  const queryClient = useQueryClient();
  return useMutation<
    NotificationDTO,
    ApiError,
    string,
    { previous?: NotificationListResponse }
  >({
    mutationFn: (id) =>
      apiClient<NotificationDTO>(`/api/notifications/${id}/read`, { method: "PATCH" }),
    // Optimistic: flip the one row read + decrement the badge, roll back on error.
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: notificationsKey });
      const previous = queryClient.getQueryData<NotificationListResponse>(notificationsKey);
      if (previous) {
        const now = new Date().toISOString();
        const items = previous.items.map((n) =>
          n.id === id && !n.readAt ? { ...n, readAt: now } : n,
        );
        queryClient.setQueryData<NotificationListResponse>(notificationsKey, {
          items,
          unreadCount: unreadIn(items),
        });
      }
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(notificationsKey, ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationsKey });
    },
  });
}

export function useMarkAllRead() {
  const queryClient = useQueryClient();
  return useMutation<
    { updated: number },
    ApiError,
    void,
    { previous?: NotificationListResponse }
  >({
    mutationFn: () =>
      apiClient<{ updated: number }>("/api/notifications/read-all", { method: "POST" }),
    // Optimistic: mark everything read + zero the badge immediately.
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: notificationsKey });
      const previous = queryClient.getQueryData<NotificationListResponse>(notificationsKey);
      if (previous) {
        const now = new Date().toISOString();
        queryClient.setQueryData<NotificationListResponse>(notificationsKey, {
          items: previous.items.map((n) => (n.readAt ? n : { ...n, readAt: now })),
          unreadCount: 0,
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(notificationsKey, ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationsKey });
    },
  });
}
